import { adfListItemChildrenToHtml } from './adfListItemChildrenToHtml';
import { asLooseNode } from './asLooseNode';
import { cardToHtml } from './cardToHtml';
import { escapeHtml } from './escapeHtml';
import { escapeHtmlAttr } from './escapeHtmlAttr';
import { isHttpUrl } from './isHttpUrl';
import { isSafeUrl } from './isSafeUrl';

/**
 * Converts an ADF DocNode JSON tree to lightweight clipboard HTML.
 *
 * Produces semantic HTML that the editor paste pipeline can materialise back
 * into structured ADF nodes (mentions, Smart Links, headings, lists, marks).
 * Unknown containers fall back to their serialised children; unknown text
 * leaves fall back to escaped text.
 */
export const adfNodeToHtml = (node: unknown): string => {
	const n = asLooseNode(node);

	switch (n.type) {
		case 'doc':
			return (n.content ?? []).map(adfNodeToHtml).join('');

		case 'paragraph':
			return `<p>${(n.content ?? []).map(adfNodeToHtml).join('')}</p>`;

		case 'heading': {
			// Clamp level to the valid HTML heading range so attacker-controlled or
			// malformed ADF cannot produce nonsense tags like <h7> or <hX>.
			const rawLevel = n.attrs?.level;
			const level =
				typeof rawLevel === 'number' && rawLevel >= 1 && rawLevel <= 6 ? Math.floor(rawLevel) : 1;
			return `<h${level}>${(n.content ?? []).map(adfNodeToHtml).join('')}</h${level}>`;
		}

		case 'bulletList':
			return `<ul>${(n.content ?? []).map(adfNodeToHtml).join('')}</ul>`;

		case 'orderedList': {
			const rawOrder = n.attrs?.order;
			const order =
				typeof rawOrder === 'number' && Number.isFinite(rawOrder) && rawOrder > 1
					? Math.floor(rawOrder)
					: undefined;
			const startAttr = order ? ` start="${order}"` : '';
			return `<ol${startAttr}>${(n.content ?? []).map(adfNodeToHtml).join('')}</ol>`;
		}

		case 'listItem':
			return `<li>${adfListItemChildrenToHtml(n.content ?? [])}</li>`;

		case 'codeBlock':
			return `<pre><code>${(n.content ?? []).map(adfNodeToHtml).join('')}</code></pre>`;

		case 'hardBreak':
			return '<br>';

		case 'inlineCard':
		case 'blockCard':
		case 'embedCard': {
			const url = String(n.attrs?.url ?? '');
			// Smart-link cards only resolve http(s) URLs. Reject anything else
			// (javascript:, data:, mailto:, relative, #frag) so a malformed ADF
			// can neither land a dangerous URL on the clipboard nor produce an
			// unresolvable card.
			const safeUrl = isHttpUrl(url) ? url : '';
			return cardToHtml(n.type, safeUrl, url);
		}
		case 'mention': {
			// data-mention-id lets the paste pipeline reconstruct an ADF mention.
			// Escape all interpolated values because clipboard HTML may be
			// pasted into unsanitised targets.
			const id = String(n.attrs?.id ?? '');
			const text = String(n.attrs?.text ?? `@${id}`);
			const userType = String(n.attrs?.userType ?? 'DEFAULT');
			return `<span data-mention-id="${escapeHtmlAttr(id)}" data-user-type="${escapeHtmlAttr(userType)}" contenteditable="false">${escapeHtml(text)}</span>`;
		}

		case 'text': {
			let html = escapeHtml(n.text ?? '');
			const marks = n.marks ?? [];
			for (const mark of marks) {
				if (mark.type === 'strong') {
					html = `<strong>${html}</strong>`;
				} else if (mark.type === 'em') {
					html = `<em>${html}</em>`;
				} else if (mark.type === 'underline') {
					html = `<u>${html}</u>`;
				} else if (mark.type === 'strike') {
					html = `<s>${html}</s>`;
				} else if (mark.type === 'code') {
					html = `<code>${html}</code>`;
				} else if (mark.type === 'link') {
					const href = String(mark.attrs?.href ?? '');
					const safeHref = isSafeUrl(href) ? href : '';
					html = `<a href="${escapeHtmlAttr(safeHref)}">${html}</a>`;
				}
			}
			return html;
		}

		default:
			if (Array.isArray(n.content) && n.content.length > 0) {
				return n.content.map(adfNodeToHtml).join('');
			}
			return n.text ? escapeHtml(n.text) : '';
	}
};
